// src/hooks/useOrdersData.js
import { useState, useEffect } from "react";
import { pb } from "../services/pocketbase";
import { useAuth } from "../contexts/AuthContext";

export function useOrdersData() {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setRawData([]);
      setLoading(true);
      return;
    }

    // 1. Fetch Inicial (Logística vê do mais novo pro antigo: sort="-created")
    const fetchOrders = async () => {
      try {
        const records = await pb.collection("production_orders").getFullList({
          sort: "-created",
          filter: "archived != true",
          expand: "fiscal_invoices_via_order",
        });

        // Mapeia full_data
        const mapped = records.map(r => ({
           ...r.full_data, 
           ...r, 
           id: r.id,
           expand: r.expand || {},
           exception: r.specs?.exception || r.exception || r.full_data?.exception || ""
         }));
        setRawData(mapped);
        setLoading(false);
      } catch (e) {
        console.error("Erro fetch logistics:", e);
        setLoading(false);
      }
    };

    fetchOrders();

    // 2. Realtime - Orders
    pb.collection("production_orders").subscribe("*", (e) => {
      setRawData(prev => {
        const record = { 
          ...e.record.full_data, 
          ...e.record, 
          id: e.record.id,
          expand: e.record.expand || {},
          exception: e.record.specs?.exception || e.record.exception || e.record.full_data?.exception || ""
        };

        // DELETE ou ARQUIVADO
        if (e.action === "delete" || record.archived) {
          return prev.filter(o => o.id !== record.id);
        }

        // CREATE
        if (e.action === "create") {
          if (!record.archived) return [record, ...prev]; // Mais novo no topo
        }

        // UPDATE
        if (e.action === "update") {
          return prev.map(o => o.id === record.id ? { ...o, ...record, expand: { ...o.expand, ...record.expand } } : o);
        }

        return prev;
      });
    });

    // 3. Realtime - Fiscal Invoices
    pb.collection("fiscal_invoices").subscribe("*", (e) => {
      setRawData(prev => {
        const invoice = e.record;
        const orderId = Array.isArray(invoice.order) ? invoice.order[0] : invoice.order;
        if (!orderId) return prev;

        if (e.action === "delete") {
          return prev.map(o => {
            if (o.id === orderId) {
              const invoicesList = (o.expand?.fiscal_invoices_via_order || []).filter(i => i.id !== invoice.id);
              return {
                ...o,
                expand: {
                  ...o.expand,
                  fiscal_invoices_via_order: invoicesList
                }
              };
            }
            return o;
          });
        }

        if (e.action === "update" || e.action === "create") {
          return prev.map(o => {
            if (o.id === orderId) {
              const invoicesList = o.expand?.fiscal_invoices_via_order || [];
              const exists = invoicesList.some(i => i.id === invoice.id);
              const newList = exists 
                ? invoicesList.map(i => i.id === invoice.id ? invoice : i)
                : [...invoicesList, invoice];
              return {
                ...o,
                expand: {
                  ...o.expand,
                  fiscal_invoices_via_order: newList
                }
              };
            }
            return o;
          });
        }

        return prev;
      });
    });

    return () => {
      pb.collection("production_orders").unsubscribe("*").catch(() => {});
      pb.collection("fiscal_invoices").unsubscribe("*").catch(() => {});
    };
  }, [user]);

  return { rawData, loading, setRawData };
}
